import { ApiRequestLog, ApiLogFilterOptions } from "./types";
import { getClientSession } from "@/services/auth/clientSession";
import { API_ENDPOINTS } from "@/config/api";
import { secureRandom, secureRandomInt, secureRandomString } from "@/utils/secureRandom";
import { buildApiLogQueryParams, truncateApiLogBody } from "./apiMonitoringHelpers";

/** Sample API logs are for local development only — never in production monitoring. */
export const shouldUseSampleApiLogs = (): boolean => !import.meta.env.PROD;

const resolveEmptyApiLogs = (
  count: number,
  currentUser?: { id?: string; email?: string }
): ApiRequestLog[] => {
  if (shouldUseSampleApiLogs()) {
    return generateSampleApiLogs(count, currentUser);
  }
  return [];
};

/** Gateway returns this stub on proxy errors — not a log array. */
export function isSystemProxyStubResponse(result: unknown): boolean {
  if (!result || typeof result !== "object" || Array.isArray(result)) {
    return false;
  }
  const obj = result as { status?: string; message?: string; data?: unknown };
  return obj.status === "ok" && typeof obj.message === "string" && !Array.isArray(obj.data);
}

/** Normalize system-service /api-requests JSON into raw log rows. */
export function parseApiRequestLogsResponse(result: unknown): Record<string, unknown>[] {
  if (isSystemProxyStubResponse(result)) {
    return [];
  }

  if (Array.isArray(result)) {
    return result as Record<string, unknown>[];
  }

  if (result && typeof result === "object" && result !== null) {
    const { data } = result as { data?: unknown };
    if (Array.isArray(data)) {
      return data as Record<string, unknown>[];
    }
  }

  return [];
}

/** Map a system-service api_request_logs row to the UI model. */
export function mapApiRequestLogRow(log: Record<string, unknown>): ApiRequestLog {
  const statusCode = log.status_code ?? log.status;
  const durationMs = log.duration_ms ?? log.duration;
  const asLogString = (value: unknown, fallback: string): string => {
    if (typeof value === "string") return value;
    if (typeof value === "number" || typeof value === "boolean") return String(value);
    return fallback;
  };

  return {
    id: asLogString(log.id ?? log.uuid, `log-${Date.now()}-${secureRandomString(8)}`),
    timestamp: asLogString(log.timestamp ?? log.created_at, new Date().toISOString()),
    method: asLogString(log.method, "UNKNOWN"),
    endpoint: asLogString(log.endpoint, ""),
    status: Number(statusCode ?? 0),
    duration: Number(durationMs ?? 0),
    userId: (log.user_id || log.userId) as string | undefined,
    userEmail: (log.user_email || log.userEmail) as string | undefined,
    ipAddress: (log.ip_address || log.ipAddress) as string | undefined,
    userAgent: (log.user_agent || log.userAgent) as string | undefined,
    requestBody: (log.request_body || log.requestBody) as ApiRequestLog["requestBody"],
    responseBody: (log.response_body || log.responseBody) as ApiRequestLog["responseBody"],
    error: log.error as string | undefined,
    origin: log.origin as string | undefined,
    createdAt: asLogString(
      log.created_at || log.createdAt || log.timestamp,
      new Date().toISOString()
    ),
  };
}

/**
 * Synchronizes API request logs with filtering options.
 * @example
 * sync(100, { status: 'success', method: 'GET' })
 * // Returns a Promise resolved with filtered API logs.
 * @param {number} limit - The maximum number of logs to retrieve from the database, defaults to 150.
 * @param {ApiLogFilterOptions} [filters] - Optional filters to apply on the logs, such as status and method.
 * @returns {Promise<ApiRequestLog[]>} Promise that resolves to an array of ApiRequestLog objects.
 * @description
 *   - Requires an active authenticated session to retrieve logs.
 *   - Logs are filtered based on status, method, endpoint, time range, duration, and a general search query.
 *   - If no data is found and no filters are applied, it generates sample data using current user information.
 *   - Errors during the database query result in the generation of sample logs for current user if possible.
 */
async function resolveAccessToken(accessToken?: string): Promise<string | null> {
  if (accessToken) {
    return accessToken;
  }

  for (let attempt = 0; attempt < 3; attempt += 1) {
    const { data: sessionData } = await getClientSession();
    if (sessionData.session?.access_token) {
      return sessionData.session.access_token;
    }
    await new Promise((resolve) => setTimeout(resolve, 200 * (attempt + 1)));
  }

  return null;
}

/**
 * Fetch Api Request Logs.
 */
export const fetchApiRequestLogs = async (
  limit: number = 150,
  filters?: ApiLogFilterOptions,
  accessToken?: string
): Promise<ApiRequestLog[]> => {
  const token = await resolveAccessToken(accessToken);
  if (!token) {
    throw new Error("No active session");
  }

  const queryParams = buildApiLogQueryParams(limit, filters);

  const controller = new AbortController();
  const timeoutMs = 10000;
  const timeoutId = setTimeout(() => controller.abort(), timeoutMs);

  try {
    const response = await fetch(`/api/system/api-requests?${queryParams.toString()}`, {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      credentials: "include",
      signal: controller.signal,
    });

    let result: unknown;
    try {
      result = await response.json();
    } catch (parseError) {
      console.error("Error parsing API response:", parseError);
      throw new Error("Failed to parse API request logs response");
    }

    if (!response.ok || isSystemProxyStubResponse(result)) {
      const resultObj = result as { error?: string; message?: string } | null;
      const errorMsg = resultObj?.error || resultObj?.message || "Failed to fetch API request logs";
      console.error("Error fetching API logs:", errorMsg, { status: response.status });
      throw new Error(errorMsg);
    }

    const logs = parseApiRequestLogsResponse(result).map(mapApiRequestLogRow);

    if (logs.length === 0) {
      if (shouldUseSampleApiLogs()) {
        console.warn("No API logs found, generating sample data (dev only)");
      }
      return resolveEmptyApiLogs(10);
    }

    return logs;
  } catch (error) {
    if (
      error instanceof Error &&
      (error.name === "AbortError" || error.message.includes("aborted"))
    ) {
      console.warn("API request timed out");
      throw new Error("API request timed out");
    }

    throw error;
  } finally {
    clearTimeout(timeoutId);
  }
};

// Add this function to track API requests in real-time
/**
 * Logs API request data to the database with additional user session information.
 * @example
 * sync({
 *   method: 'GET',
 *   endpoint: '/api/example',
 *   status: 200,
 *   duration: 150,
 *   ipAddress: '192.168.1.1',
 *   userAgent: 'Mozilla/5.0',
 *   requestBody: '{}',
 *   responseBody: '{}',
 *   error: '',
 *   timestamp: '2023-10-21T14:48:00.000Z'
 * })
 * @param {Partial<ApiRequestLog>} requestData - The data related to the API request which is to be logged.
 * @returns {Promise<void>} A promise that resolves when the log operation is complete.
 * @description
 *   - Attempts to retrieve the user's session info using Supabase before logging the request.
 *   - Defaults to 'UNKNOWN' for `method` and `endpoint` if not provided in requestData.
 *   - Ensures logging even if user session retrieval fails.
 *   - Logs any errors encountered during the logging process to console.
 */
// Helper function to truncate large strings to prevent 413 errors
const truncateBody = truncateApiLogBody;

/**
 * Persist Api Request Log.
 */
async function persistApiRequestLog(
  requestData: Partial<ApiRequestLog>,
  userId: string,
  userEmail: string | undefined
): Promise<void> {
  const truncatedRequestBody = truncateBody(requestData.requestBody);
  const truncatedResponseBody = truncateBody(requestData.responseBody);

  const logData = {
    method: requestData.method || "UNKNOWN",
    endpoint: requestData.endpoint || "UNKNOWN",
    status_code: requestData.status || 0,
    duration_ms: requestData.duration || 0,
    user_id: userId,
    user_email: userEmail,
    ip_address: requestData.ipAddress || null,
    user_agent: requestData.userAgent || null,
    request_body: truncatedRequestBody,
    response_body: truncatedResponseBody,
    error: requestData.error || null,
    timestamp: new Date().toISOString(),
  };

  const { data: sessionData } = await getClientSession();
  if (!sessionData.session) {
    return;
  }

  const payload = {
    method: logData.method,
    endpoint: logData.endpoint,
    status: logData.status_code,
    duration: logData.duration_ms,
    userId: logData.user_id,
    userEmail: logData.user_email,
    ipAddress: logData.ip_address,
    userAgent: logData.user_agent,
    requestBody: logData.request_body,
    responseBody: logData.response_body,
    error: logData.error,
  };

  const payloadString = JSON.stringify(payload);
  const MAX_PAYLOAD_SIZE = 100 * 1024;
  if (payloadString.length > MAX_PAYLOAD_SIZE) {
    return;
  }

  try {
    const response = await fetch(API_ENDPOINTS.SYSTEM.API_REQUESTS, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${sessionData.session.access_token}`,
      },
      credentials: "include",
      body: payloadString,
    });

    if (!response.ok) {
      return;
    }
  } catch (fetchError: unknown) {
    console.error("Non-critical API tracking fetch failed:", fetchError);
  }
}

/**
 * Execute Track Api Request.
 */
async function executeTrackApiRequest(requestData: Partial<ApiRequestLog>): Promise<void> {
  const { isAdminOrSuperAdmin } = await import("@/utils/adminCheck");
  const isAdmin = await isAdminOrSuperAdmin();
  if (!isAdmin) {
    return;
  }

  let userId: string | undefined;
  let userEmail: string | undefined;

  try {
    const { data, error } = await getClientSession();
    if (data?.session?.user && !error) {
      userId = data.session.user.id;
      userEmail = data.session.user.email;
    }
  } catch (err) {
    console.error("Operation failed:", err);
  }

  if (!userId) {
    return;
  }

  try {
    await persistApiRequestLog(requestData, userId, userEmail);
  } catch (dbError) {
    console.error("Operation failed:", dbError);
  }
}

// Fire-and-forget API tracking - never blocks or throws errors
export const trackApiRequest = (requestData: Partial<ApiRequestLog>): void => {
  setTimeout(() => {
    executeTrackApiRequest(requestData).catch((error) => {
      console.error("Operation failed:", error);
    });
  }, 0);
};

// Function to generate sample API logs for demonstration - now with current user info
/**
 * Generates a list of sample API request logs.
 * @example
 * countApiLogs(5, { id: 'user123', email: 'email@example.com' })
 * [
 *   { id: 'sample-1', timestamp: '2023-09-30T08:10:00.000Z', ... },
 *   ...
 * ]
 * @param {number} count - The number of logs to generate.
 * @param {any} currentUser (optional) - User information to be used in the logs.
 * @returns {ApiRequestLog[]} An array of API request logs.
 * @description
 *   - Randomizes the method, endpoint, status, and duration for each log entry.
 *   - Uses the current user's information if provided; otherwise, uses default sample data.
 *   - Generates a timestamp based on the current time, decreasing for each successive log.
 *   - Includes error messages for logs with status codes indicating a client or server error.
 */
const generateSampleApiLogs = (
  count: number,
  currentUser?: { id?: string; email?: string }
): ApiRequestLog[] => {
  const methods = ["GET", "POST", "PUT", "DELETE"];
  const endpoints = [
    "/api/auth/user",
    "/api/assets/list",
    "/api/transactions/recent",
    "/api/settings/preferences",
    "/api/goals/progress",
    "/api/subscriptions/status",
  ];
  const statusCodes = [200, 201, 400, 401, 403, 404, 500];
  const now = new Date();

  // Use current user info if available, otherwise use sample data
  const userId = currentUser?.id || "sample-user-id";
  const userEmail = currentUser?.email || "sample@example.com";

  return Array.from({ length: count }, (_, index) => {
    const method = methods[secureRandomInt(methods.length)];
    const endpoint = endpoints[secureRandomInt(endpoints.length)];
    const status = statusCodes[secureRandomInt(statusCodes.length)];
    const duration = secureRandomInt(300) + 20; // 20ms to 320ms
    const timestamp = new Date(
      now.getTime() - index * 60000 - secureRandom() * 3600000
    ).toISOString();

    // Create sample message for errors
    let message = undefined;
    if (status >= 400) {
      message = `Error ${status} on ${method} ${endpoint}`;
    }

    return {
      id: `sample-${crypto.randomUUID()}`,
      timestamp,
      method,
      endpoint,
      status,
      duration,
      ipAddress: "127.0.0.1",
      userAgent: navigator.userAgent,
      userId,
      userEmail,
      origin: "web-app",
      message,
      createdAt: timestamp,
    };
  });
};
